// client/src/pages/admin/ManageSettings.js
// Admin settings page with friendly labels, logical grouping,
// and proper UI controls for each setting type.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Button from '../../components/ui/Button.js';

// ─── Friendly labels and descriptions for each setting key ──
// If a setting key isn't listed here, it falls back to the raw key
// and the database description.
const SETTING_META = {
  site_name: {
    label: 'Site Name',
    description: 'The name of your website, shown in emails and the browser tab.',
    icon: '🏷️',
  },
  cards_display_name: {
    label: 'Cards Display Name',
    description: 'What digital cards are called throughout the site (e.g., "eCards", "Moonbeams").',
    icon: '💌',
  },
  stories_display_name: {
    label: 'Stories Display Name',
    description: 'What the stories section is called (e.g., "Stories", "Tales").',
    icon: '📖',
  },
  home_community_text: {
    label: 'Homepage Community Text',
    description: 'The paragraph shown under the "Moonbeams Shared" tally on the homepage. The tally number itself is automatic — this is just the wording beneath it.',
    icon: '✨',
  },
  home_tally_metric: {
    label: 'Tally Number Shows',
    description: 'Which number the homepage tally box displays. Hybrid = shareable links count as 1 each + each email recipient counts. Cards = every moonbeam counts as 1. Deliveries = each recipient counts. Views = total times moonbeams were opened.',
    icon: '🔢',
    options: [
      { value: 'hybrid', label: 'Hybrid (links = 1 + email deliveries)' },
      { value: 'cards', label: 'Cards (every moonbeam = 1)' },
      { value: 'deliveries', label: 'Deliveries (each recipient counts)' },
      { value: 'views', label: 'Views (total opens)' },
    ],
  },
  home_hero_welcome: {
    label: 'Hero — Welcome Line',
    description: 'Small italic line above the site name in the hero.',
    icon: '👋',
  },
  home_hero_browse_button: {
    label: 'Hero — Browse Button',
    description: 'Hero button to browse moonbeams. Use {cards} for the cards display name.',
    icon: '🔘',
  },
  home_hero_stories_button: {
    label: 'Hero — Stories Button',
    description: 'Hero button to read stories. Use {stories} for the stories display name.',
    icon: '🔘',
  },
  home_tally_heading: {
    label: 'Tally — Heading',
    description: 'Heading above the community tally number.',
    icon: '📣',
  },
  home_tally_label: {
    label: 'Tally — Caption',
    description: 'Caption shown beneath the tally number.',
    icon: '🏷️',
  },
  home_stories_heading: {
    label: 'Stories — Heading',
    description: 'Heading for the stories section.',
    icon: '📚',
  },
  home_stories_intro: {
    label: 'Stories — Intro',
    description: 'Intro paragraph for the stories section.',
    icon: '📝',
  },
  home_stories_button_1: {
    label: 'Stories — Button 1 Label',
    description: 'Label for the first (Classics) story button.',
    icon: '🔘',
  },
  home_stories_button_2: {
    label: 'Stories — Button 2 Label',
    description: 'Label for the second (Chronicles) story button.',
    icon: '🔘',
  },
  home_newsletter_heading: {
    label: 'Newsletter — Heading',
    description: 'Heading for the newsletter subscribe section.',
    icon: '📣',
  },
  home_newsletter_text: {
    label: 'Newsletter — Description',
    description: 'Description paragraph for the newsletter subscribe section.',
    icon: '📝',
  },
  home_footer_text: {
    label: 'Homepage Footer Line',
    description: 'Small italic line in the homepage footer divider (between the ✦ marks).',
    icon: '✦',
  },
  default_trial_tier: {
    label: 'Default Trial Tier',
    description: 'Which plan new signups auto-trial into. Set to 0 to disable auto-trial.',
    icon: '🎁',
  },
  business_templates_enabled: {
    label: 'Business Templates Visible',
    description: 'Show the Business tab and business-only eCard templates to business-tier users. When off, business templates are hidden from everyone except admins.',
    icon: '💼',
  },
  gift_enabled: {
    label: 'Gift Subscriptions',
    description: 'Master switch for the Gift a Plan feature. When off, users cannot purchase gift subscriptions.',
    icon: '🎁',
  },
  gift_code_expiry_days: {
    label: 'Gift Code Expiry (Days)',
    description: 'How many days a gift code remains valid for redemption after purchase.',
    icon: '⏳',
  },
  gift_allowed_intervals: {
    label: 'Gift Allowed Intervals',
    description: 'Which billing intervals can be gifted (JSON array, e.g., ["yearly"]).',
    icon: '📅',
  },
  gift_allowed_tiers: {
    label: 'Giftable Plan Tiers',
    description: 'Which user type IDs (plans) can be gifted (JSON array, e.g., [3, 4]).',
    icon: '🎯',
  },
  marketing_send_rate_per_second: {
    label: 'Marketing Send Rate',
    description: 'Maximum marketing emails sent per second. Keep low to stay within email provider limits.',
    icon: '⚡',
  },
  marketing_batch_size: {
    label: 'Marketing Batch Size',
    description: 'Number of marketing recipients processed per background job tick before yielding.',
    icon: '📦',
  },
  system_email_provider: {
    label: 'System Email Provider',
    description: 'Which email provider sends verification codes and password resets. Options: "resend" or "elastic". Make sure the matching API key is set in your environment variables.',
    icon: '📨',
  },
  system_email_from_address: {
    label: 'System Email From Address',
    description: 'From address for verification and password reset emails (e.g., "hello@notify.thecherryfairyshop.com"). Leave blank to use the default from your environment.',
    icon: '📬',
  },
  system_email_from_name: {
    label: 'System Email From Name',
    description: 'Display name for system emails (e.g., "Cherry Fairy Account"). Leave blank to use the default from your environment.',
    icon: '🏷️',
  },
};

// ─── Group definitions ──────────────────────────────────────
// Each group has a key, label, icon, description, and a list of
// setting keys that belong to it. Settings not in any group go
// to "Other".
const SETTING_GROUPS = [
  {
    key: 'branding',
    label: 'Branding & Display',
    icon: '🎨',
    description: 'Site name and how things are labeled across your app.',
    keys: ['site_name', 'cards_display_name', 'stories_display_name'],
  },
  {
    key: 'homepage',
    label: 'Homepage Text & Tally',
    icon: '🏠',
    description: 'Edit the wording of every section on the homepage, and choose which number the community tally box displays.',
    keys: [
      'home_hero_welcome', 'home_hero_browse_button', 'home_hero_stories_button',
      'home_tally_heading', 'home_tally_metric', 'home_tally_label', 'home_community_text',
      'home_stories_heading', 'home_stories_intro', 'home_stories_button_1', 'home_stories_button_2',
      'home_newsletter_heading', 'home_newsletter_text',
      'home_footer_text',
    ],
  },
  {
    key: 'features',
    label: 'Feature Toggles',
    icon: '⚙️',
    description: 'Turn major features on or off for all users.',
    keys: ['business_templates_enabled', 'default_trial_tier'],
  },
  {
    key: 'gifts',
    label: 'Gift Subscriptions',
    icon: '🎁',
    description: 'Configure the Gift a Plan feature.',
    keys: ['gift_enabled', 'gift_code_expiry_days', 'gift_allowed_intervals', 'gift_allowed_tiers'],
  },
  {
    key: 'system_email',
    label: 'System Emails (Verification & Password Reset)',
    icon: '📨',
    description: 'Control which email provider and from address is used for verification codes, password resets, and other system emails. Change provider here to switch between Resend and Elastic Email without a code deploy.',
    keys: ['system_email_provider', 'system_email_from_address', 'system_email_from_name'],
  },
  {
    key: 'marketing',
    label: 'Marketing & Email',
    icon: '📧',
    description: 'Rate limits and batch sizes for marketing campaign sends.',
    keys: ['marketing_send_rate_per_second', 'marketing_batch_size'],
  },
];

// ─── Toggle Switch Component ────────────────────────────────
function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gold/30 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? 'bg-green-600' : 'bg-royal-600'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Setting Row Component ──────────────────────────────────
function SettingRow({ setting, value, onChange }) {
  const meta = SETTING_META[setting.settingkey] || {};
  const label = meta.label || setting.settingkey;
  const description = meta.description || setting.description || '';
  const icon = meta.icon || '⚙️';

  const isBoolean = setting.settingtype === 'boolean';
  const isNumber = setting.settingtype === 'number';
  const isJson = setting.settingtype === 'json';
  // 'text' renders a multi-line textarea (for longer prose like the homepage
  // community paragraph). Plain 'string' stays a single-line input.
  const isText = setting.settingtype === 'text';
  // 'select' renders a dropdown using options from SETTING_META[key].options.
  const isSelect = setting.settingtype === 'select';
  const boolValue = value === 'true';

  // Long-form text settings get a full-width textarea BELOW the label/desc
  // rather than a narrow right-aligned input, since a paragraph needs room.
  if (isText) {
    return (
      <div className="p-4 bg-royal-800/30 border border-royal-700/50 rounded-xl hover:border-royal-600 transition-colors">
        <div className="flex items-start gap-4 mb-3">
          <div className="text-xl flex-shrink-0 mt-0.5">{icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-white text-sm font-semibold">{label}</p>
              {!setting.iseditable && (
                <span className="text-xs bg-royal-700 text-royal-300 px-2 py-0.5 rounded">Read Only</span>
              )}
            </div>
            {description && (
              <p className="text-royal-300 text-xs leading-relaxed mb-2">{description}</p>
            )}
            <p className="text-royal-500 text-xs font-mono">{setting.settingkey}</p>
          </div>
        </div>
        <textarea
          className="w-full bg-royal-800 border border-royal-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold/50 transition-colors leading-relaxed resize-y min-h-[96px]"
          value={value}
          onChange={(e) => onChange(setting.settingkey, e.target.value)}
          disabled={!setting.iseditable}
          rows={4}
        />
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4 p-4 bg-royal-800/30 border border-royal-700/50 rounded-xl hover:border-royal-600 transition-colors">
      <div className="text-xl flex-shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-white text-sm font-semibold">{label}</p>
          {!setting.iseditable && (
            <span className="text-xs bg-royal-700 text-royal-300 px-2 py-0.5 rounded">Read Only</span>
          )}
        </div>
        {description && (
          <p className="text-royal-300 text-xs leading-relaxed mb-2">{description}</p>
        )}
        <p className="text-royal-500 text-xs font-mono">{setting.settingkey}</p>
      </div>
      <div className="flex-shrink-0 mt-1">
        {isBoolean ? (
          <div className="flex items-center gap-2">
            <ToggleSwitch
              checked={boolValue}
              onChange={(newVal) => onChange(setting.settingkey, newVal ? 'true' : 'false')}
              disabled={!setting.iseditable}
            />
            <span className={`text-xs font-medium min-w-[24px] ${boolValue ? 'text-green-400' : 'text-royal-400'}`}>
              {boolValue ? 'ON' : 'OFF'}
            </span>
          </div>
        ) : isSelect ? (
          <select
            className="bg-royal-800 border border-royal-600 rounded-lg px-3 py-2 text-white text-sm w-64 focus:outline-none focus:border-gold/50 transition-colors"
            value={value}
            onChange={(e) => onChange(setting.settingkey, e.target.value)}
            disabled={!setting.iseditable}
          >
            {(meta.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : isNumber ? (
          <input
            type="number"
            className="bg-royal-800 border border-royal-600 rounded-lg px-3 py-2 text-white text-sm w-28 text-right focus:outline-none focus:border-gold/50 transition-colors"
            value={value}
            onChange={(e) => onChange(setting.settingkey, e.target.value)}
            disabled={!setting.iseditable}
          />
        ) : isJson ? (
          <input
            className="bg-royal-800 border border-royal-600 rounded-lg px-3 py-2 text-white text-sm w-48 font-mono text-right focus:outline-none focus:border-gold/50 transition-colors"
            value={value}
            onChange={(e) => onChange(setting.settingkey, e.target.value)}
            disabled={!setting.iseditable}
          />
        ) : (
          <input
            className="bg-royal-800 border border-royal-600 rounded-lg px-3 py-2 text-white text-sm w-56 text-right focus:outline-none focus:border-gold/50 transition-colors"
            value={value}
            onChange={(e) => onChange(setting.settingkey, e.target.value)}
            disabled={!setting.iseditable}
          />
        )}
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────
export default function ManageSettings() {
  const [settings, setSettings] = useState([]);
  const [modified, setModified] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    adminService.getSettings().then((d) => setSettings(d.settings));
  }, []);

  const handleChange = (key, value) => {
    setModified((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const updates = Object.entries(modified).map(([settingkey, settingvalue]) => ({ settingkey, settingvalue }));
    try {
      await adminService.updateSettings(updates);
      setMessage('Settings saved!');
      setModified({});
      adminService.getSettings().then((d) => setSettings(d.settings));
    } catch {
      setMessage('Save failed');
    }
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const hasChanges = Object.keys(modified).length > 0;

  // Build grouped settings
  const allGroupedKeys = new Set(SETTING_GROUPS.flatMap(g => g.keys));
  const otherSettings = settings.filter(s => !allGroupedKeys.has(s.settingkey));

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-gold">Settings</h1>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-amber-400 text-sm">
              {Object.keys(modified).length} unsaved change{Object.keys(modified).length !== 1 ? 's' : ''}
            </span>
          )}
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {message && (
        <div className={`mb-5 p-3 rounded-lg border text-sm ${
          message.includes('failed') 
            ? 'bg-red-900/20 border-red-600/40 text-red-400' 
            : 'bg-green-900/20 border-green-600/40 text-green-400'
        }`}>
          {message}
        </div>
      )}

      {/* Grouped Settings */}
      {SETTING_GROUPS.map((group) => {
        const groupSettings = settings.filter(s => group.keys.includes(s.settingkey));
        if (groupSettings.length === 0) return null;

        // Sort by the order defined in group.keys
        groupSettings.sort((a, b) => group.keys.indexOf(a.settingkey) - group.keys.indexOf(b.settingkey));

        return (
          <div key={group.key} className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-lg">{group.icon}</span>
              <h2 className="text-white text-lg font-semibold">{group.label}</h2>
            </div>
            {group.description && (
              <p className="text-royal-400 text-xs mb-4 ml-8">{group.description}</p>
            )}
            <div className="space-y-2">
              {groupSettings.map((s) => {
                const val = modified[s.settingkey] !== undefined ? modified[s.settingkey] : s.settingvalue;
                return (
                  <SettingRow
                    key={s.settingid}
                    setting={s}
                    value={val}
                    onChange={handleChange}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Other / Ungrouped Settings */}
      {otherSettings.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-lg">🔧</span>
            <h2 className="text-white text-lg font-semibold">Other Settings</h2>
          </div>
          <p className="text-royal-400 text-xs mb-4 ml-8">Additional configuration values.</p>
          <div className="space-y-2">
            {otherSettings.map((s) => {
              const val = modified[s.settingkey] !== undefined ? modified[s.settingkey] : s.settingvalue;
              return (
                <SettingRow
                  key={s.settingid}
                  setting={s}
                  value={val}
                  onChange={handleChange}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Sticky save bar when there are changes */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-royal-900/95 border-t border-gold/30 backdrop-blur-sm py-3 px-4 z-30">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <span className="text-amber-400 text-sm">
              {Object.keys(modified).length} unsaved change{Object.keys(modified).length !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setModified({})}
                className="px-4 py-2 text-sm text-royal-200 border border-royal-600 rounded-lg hover:text-white hover:border-royal-400 transition-colors"
              >
                Discard
              </button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}